import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import { Plus, Map, ArrowLeft, Trash2, X, MapPin, Sprout, Calendar } from 'lucide-react';
import axios from 'axios';
import EntryPill from '@/components/season-template/EntryPill';
import SeasonalTemplateOverlay from '@/components/season-template/SeasonalTemplateOverlay';
import HolographicCanvas from '@/components/planner/HolographicCanvas';
import PlannerCalendar from '@/components/planner/PlannerCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROT_COLOR = { red: '#C66B4A', orange: '#C9A85B', green: '#6B9F4A', neutral: '#8EAA97' };

// NZ (southern-hemisphere) season for the planner header.
function currentSeason() {
  const m = new Date().getMonth() + 1;
  if ([12, 1, 2].includes(m)) return 'Summer';
  if ([3, 4, 5].includes(m)) return 'Autumn';
  if ([6, 7, 8].includes(m)) return 'Winter';
  return 'Spring';
}

function HistoryModal({ gardenId, bed, cropList, nowYear, onClose, onChanged }) {
  const [records, setRecords] = useState([]);
  const [year, setYear] = useState(nowYear - 1);
  const [cropId, setCropId] = useState('');
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/rotation/history/${gardenId}`, { withCredentials: true })
      .then((r) => setRecords((r.data.by_bed && r.data.by_bed[bed.bed_id]) || []))
      .catch(() => {});
  }, [gardenId, bed.bed_id]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!cropId) return;
    setSaving(true);
    try {
      await axios.post(`${API}/rotation/history`, { garden_id: gardenId, bed_id: bed.bed_id, year, crop_id: cropId }, { withCredentials: true });
      setCropId('');
      load();
      onChanged && onChanged();
    } catch (err) { console.error('Add history failed', err); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try {
      await axios.delete(`${API}/rotation/history/${id}`, { withCredentials: true });
      load();
      onChanged && onChanged();
    } catch (err) { console.error('Delete history failed', err); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="history-modal">
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="text-xl font-bold text-[#1A4331]" style={{ fontFamily: 'Cabinet Grotesk, Outfit, sans-serif' }}>Rotation history</h3>
            <p className="text-sm text-[#7A6B57]">{bed.name} — what grew here in past seasons</p>
          </div>
          <button onClick={onClose} data-testid="history-close" className="text-[#7A6B57] hover:text-[#1A4331] p-1"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={add} className="mt-4 bg-white rounded-2xl p-4 border border-[#1A4331]/5">
          <p className="text-xs font-semibold text-[#7A6B57] uppercase tracking-wider mb-2">Add a past crop</p>
          <div className="flex gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="history-year"
              className="px-3 py-2.5 bg-[#F0F2EB] rounded-xl outline-none text-sm">
              {[1, 2, 3, 4].map((n) => <option key={n} value={nowYear - n}>{nowYear - n}</option>)}
            </select>
            <select value={cropId} onChange={(e) => setCropId(e.target.value)} data-testid="history-crop"
              className="flex-1 px-3 py-2.5 bg-[#F0F2EB] rounded-xl outline-none text-sm">
              <option value="">Choose a crop…</option>
              {cropList.map((c) => <option key={c.crop_id} value={c.crop_id}>{c.name}</option>)}
            </select>
          </div>
          <button type="submit" disabled={saving || !cropId} data-testid="history-add"
            className="mt-3 bg-[#1A4331] hover:bg-[#255C44] text-white text-sm font-semibold py-2 px-5 rounded-full transition-colors disabled:opacity-50">
            {saving ? 'Adding…' : 'Add to history'}
          </button>
        </form>

        <div className="mt-4 space-y-2">
          {records.length === 0 ? (
            <p className="text-sm text-[#7A6B57] text-center py-4" data-testid="history-empty">No imported history yet. Add what you grew here last year to start the rotation clock.</p>
          ) : records.map((r) => (
            <div key={r.history_id} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-[#1A4331]/5" data-testid={`history-row-${r.history_id}`}>
              <span className="text-xs font-bold text-[#1A4331] bg-[#F0F2EB] px-2 py-1 rounded-full">{r.year}</span>
              <span className="text-sm font-medium text-[#2D241E] flex-1">{r.crop_name}</span>
              <span className="text-[10px] font-semibold text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: ROT_COLOR.neutral }}>{r.family?.replace('ceae', '')}</span>
              <button onClick={() => remove(r.history_id)} data-testid={`history-del-${r.history_id}`} className="text-[#C66B4A]/70 hover:text-[#C66B4A]"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GardenPlanner({ user }) {
  const { t } = useTranslation();
  const [gardens, setGardens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddGarden, setShowAddGarden] = useState(false);
  const [showSeason, setShowSeason] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [plantings, setPlantings] = useState([]);
  const [newGardenName, setNewGardenName] = useState('');

  const [selected, setSelected] = useState(null); // garden object
  const [planner, setPlanner] = useState(null);
  const [plannerLoading, setPlannerLoading] = useState(false);
  const [companionMap, setCompanionMap] = useState({});
  const [successionCrops, setSuccessionCrops] = useState({});
  const [rotation, setRotation] = useState(null);
  const [rotCheck, setRotCheck] = useState({});
  const [historyBed, setHistoryBed] = useState(null);
  const [cropList, setCropList] = useState([]);

  const fetchGardens = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gardens`, { withCredentials: true });
      setGardens(response.data);
    } catch (error) {
      console.error('Error fetching gardens:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGardens(); }, [fetchGardens]);

  useEffect(() => {
    axios.get(`${API}/companion-data`, { withCredentials: true })
      .then((r) => setCompanionMap(r.data || {}))
      .catch(() => {});
    axios.get(`${API}/succession/crops`, { withCredentials: true })
      .then((r) => setSuccessionCrops(r.data || {}))
      .catch(() => {});
    axios.get(`${API}/crops`, { withCredentials: true })
      .then((r) => setCropList((r.data || []).map((c) => ({ crop_id: c.crop_id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => {});
    axios.get(`${API}/plantings`, { withCredentials: true })
      .then((r) => setPlantings(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const fetchPlanner = useCallback(async (gardenId) => {
    setPlannerLoading(true);
    try {
      const res = await axios.get(`${API}/gardens/${gardenId}/planner`, { withCredentials: true });
      setPlanner(res.data);
    } catch (error) {
      console.error('Error fetching planner:', error);
    } finally {
      setPlannerLoading(false);
    }
  }, []);

  const fetchRotation = useCallback(async (gardenId) => {
    try {
      const r = await axios.get(`${API}/rotation/garden/${gardenId}`, { withCredentials: true });
      setRotation(r.data);
    } catch (error) {
      console.error('Error fetching rotation:', error);
    }
  }, []);

  const startDrag = (cropId) => {
    setRotCheck({});
    if (selected && cropId) {
      axios.post(`${API}/rotation/check`, { garden_id: selected.garden_id, crop_id: cropId }, { withCredentials: true })
        .then((r) => setRotCheck(r.data.beds || {}))
        .catch(() => {});
    }
  };
  const endDrag = () => { setRotCheck({}); };

  const openGarden = (garden) => {
    setSelected(garden);
    setPlanner(null);
    setRotation(null);
    setHistoryBed(null);
    fetchPlanner(garden.garden_id);
    fetchRotation(garden.garden_id);
  };

  const handleAddGarden = async (e) => {
    e.preventDefault();
    if (!newGardenName.trim()) return;
    try {
      await axios.post(`${API}/gardens`, { name: newGardenName }, { withCredentials: true });
      setNewGardenName('');
      setShowAddGarden(false);
      fetchGardens();
    } catch (error) {
      console.error('Error adding garden:', error);
    }
  };

  const handleDeleteBed = async (bedId) => {
    try {
      await axios.delete(`${API}/beds/${bedId}`, { withCredentials: true });
      fetchPlanner(selected.garden_id);
    } catch (error) {
      console.error('Error deleting bed:', error);
    }
  };

  const handleAutoFill = async (bedId) => {
    try {
      const res = await axios.post(`${API}/beds/${bedId}/auto-fill`, {}, { withCredentials: true });
      fetchPlanner(selected.garden_id);
      fetchRotation(selected.garden_id);
      return res.data;
    } catch (error) {
      console.error('Error auto-filling bed:', error);
      return { summary: 'Could not auto-fill this bed right now.' };
    }
  };

  const handleClearBed = async (bedId) => {
    try {
      await axios.delete(`${API}/beds/${bedId}/crops`, { withCredentials: true });
      fetchPlanner(selected.garden_id);
      fetchRotation(selected.garden_id);
    } catch (error) {
      console.error('Error clearing bed:', error);
    }
  };

  const handleCreateBedFromLayout = async (layout) => {
    if (!selected) return;
    const n = (planner?.beds?.length || 0) + 1;
    const name = n <= 26 ? `Bed ${String.fromCharCode(64 + n)}` : `Bed ${n}`;
    try {
      await axios.post(`${API}/beds`, { garden_id: selected.garden_id, name, layout }, { withCredentials: true });
      fetchPlanner(selected.garden_id);
      fetchRotation(selected.garden_id);
    } catch (error) {
      console.error('Error creating bed:', error);
    }
  };

  const handleRenameBed = async (bedId, name) => {
    setPlanner((p) => (p ? { ...p, beds: p.beds.map((b) => (b.bed_id === bedId ? { ...b, name } : b)) } : p));
    try {
      await axios.patch(`${API}/beds/${bedId}`, { name }, { withCredentials: true });
    } catch (error) {
      console.error('Error renaming bed:', error);
    }
  };

  const handleUpdateBedSize = async (bedId, size) => {
    setPlanner((p) => (p ? { ...p, beds: p.beds.map((b) => (b.bed_id === bedId ? { ...b, size } : b)) } : p));
    try {
      await axios.patch(`${API}/beds/${bedId}`, { size }, { withCredentials: true });
      // Silent refetch (no loading spinner) so the canvas stays mounted and the
      // bed-detail panel keeps its selection — parity with mobile.
      const res = await axios.get(`${API}/gardens/${selected.garden_id}/planner`, { withCredentials: true });
      setPlanner(res.data);
    } catch (error) {
      console.error('Error updating bed size:', error);
    }
  };

  const handlePersistLayout = async (bedId, layout) => {
    setPlanner((p) => (p ? { ...p, beds: p.beds.map((b) => (b.bed_id === bedId ? { ...b, layout } : b)) } : p));
    try {
      await axios.patch(`${API}/beds/${bedId}`, { layout }, { withCredentials: true });
    } catch (error) {
      console.error('Error saving bed layout:', error);
    }
  };

  const assignCrop = async (pid, bedId) => {
    setRotCheck({});
    try {
      await axios.patch(`${API}/plantings/${pid}`, { bed_id: bedId, garden_id: selected.garden_id }, { withCredentials: true });
      fetchPlanner(selected.garden_id);
      fetchRotation(selected.garden_id);
    } catch (error) {
      console.error('Error assigning crop:', error);
    }
  };

  const unassignCrop = async (pid) => {
    setRotCheck({});
    try {
      await axios.patch(`${API}/plantings/${pid}`, { bed_id: null }, { withCredentials: true });
      fetchPlanner(selected.garden_id);
      fetchRotation(selected.garden_id);
    } catch (error) {
      console.error('Error unassigning crop:', error);
    }
  };

  const rotationBeds = {};
  (rotation?.beds || []).forEach((b) => { rotationBeds[b.bed_id] = b; });

  const locationTag = selected?.location || user?.location || null;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {!selected ? (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, Outfit, sans-serif' }}>
                  {t('planner.title')}
                </h1>
                <p className="text-base text-[#5C665C] mt-2 leading-relaxed">
                  Lay out your beds, place crops, and plan spacing visually
                </p>
              </div>
              <button onClick={() => setShowAddGarden(true)} data-testid="add-garden-button"
                className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-6 rounded-full transition-colors flex items-center gap-2">
                <Plus className="w-5 h-5" /> Add Garden
              </button>
            </div>

            <EntryPill onClick={() => setShowSeason(true)} />

            <button onClick={() => setShowCalendar(true)} data-testid="view-calendar-pill"
              className="w-full sm:w-auto flex items-center justify-center gap-2.5 px-5 py-3 rounded-full transition-transform hover:-translate-y-px active:translate-y-0"
              style={{
                background: 'linear-gradient(180deg,#1E4E3A,#163a2b)',
                border: '1.5px solid rgba(201,168,91,0.34)',
                boxShadow: '0 10px 26px rgba(20,42,32,0.32), inset 0 1px 0 rgba(255,255,255,0.08)',
              }}>
              <Calendar className="w-[18px] h-[18px]" style={{ color: '#D4AF37' }} />
              <b className="text-[15px] font-bold" style={{ color: '#FAF6EC', fontFamily: 'Outfit, sans-serif', letterSpacing: '0.01em' }}>View Calendar</b>
              <span className="text-[11.5px] font-bold px-2.5 py-0.5 rounded-full"
                style={{ color: 'rgba(250,246,236,0.78)', background: 'rgba(201,168,91,0.18)', border: '1px solid rgba(201,168,91,0.34)' }}>
                {plantings.filter((p) => p.sowing_date).length} planned
              </span>
            </button>

            {showAddGarden && (
              <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
                <h3 className="text-xl font-semibold text-[#1A4331] mb-4">Create New Garden</h3>
                <form onSubmit={handleAddGarden} className="space-y-4">
                  <input type="text" value={newGardenName} onChange={(e) => setNewGardenName(e.target.value)}
                    placeholder="e.g., Front Yard, Backyard Beds, Greenhouse" data-testid="garden-name-input"
                    className="w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#8EAA97] focus:ring-2 focus:ring-[#8EAA97]/20 rounded-xl outline-none transition-all" />
                  <div className="flex gap-3">
                    <button type="submit" data-testid="save-garden-button" className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-6 rounded-full transition-colors">Create Garden</button>
                    <button type="button" onClick={() => { setShowAddGarden(false); setNewGardenName(''); }} data-testid="cancel-garden-button"
                      className="border-2 border-[#8EAA97] text-[#1A4331] font-semibold py-3 px-6 rounded-full hover:bg-[#8EAA97] hover:text-white transition-all">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-96">
                <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div>
              </div>
            ) : gardens.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 border border-[#1A4331]/5 shadow-sm text-center">
                <Map className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[#2D241E] mb-2">No gardens yet</h3>
                <p className="text-[#5C665C] mb-6">Create your first garden to start planning your crops</p>
                <button onClick={() => setShowAddGarden(true)} data-testid="create-first-garden-button"
                  className="inline-block bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-8 rounded-full transition-colors">Create Your First Garden</button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {gardens.map((garden) => (
                  <button key={garden.garden_id} onClick={() => openGarden(garden)} data-testid={`garden-card-${garden.garden_id}`}
                    className="text-left bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm card-hover">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-[#8EAA97]/10 flex items-center justify-center">
                        <Map className="w-6 h-6 text-[#1A4331]" />
                      </div>
                      <h3 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Cabinet Grotesk, Outfit, sans-serif' }}>{garden.name}</h3>
                    </div>
                    {garden.location && <p className="text-sm text-[#5C665C] mb-2">📍 {garden.location}</p>}
                    {garden.description && <p className="text-sm text-[#5C665C]">{garden.description}</p>}
                    <div className="mt-4 pt-4 border-t border-[#1A4331]/5">
                      <span className="text-sm font-semibold text-[#1A4331]">Open planner →</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="bg-gradient-to-br from-[#8EAA97]/10 to-[#B4D330]/10 rounded-3xl p-6 border border-[#8EAA97]/20">
              <h3 className="font-semibold text-[#2D241E] mb-3">🌱 Garden Planning Tips</h3>
              <ul className="space-y-2 text-sm text-[#5C665C]">
                <li className="flex items-start gap-2"><span className="text-[#1A4331] mt-0.5">•</span><span>Group crops by water needs and sun exposure</span></li>
                <li className="flex items-start gap-2"><span className="text-[#1A4331] mt-0.5">•</span><span>Plan for companion planting to improve growth and reduce pests</span></li>
                <li className="flex items-start gap-2"><span className="text-[#1A4331] mt-0.5">•</span><span>Rotate crop families each season to maintain soil health</span></li>
                <li className="flex items-start gap-2"><span className="text-[#1A4331] mt-0.5">•</span><span>Leave paths between beds for easy access and maintenance</span></li>
              </ul>
            </div>
          </>
        ) : (
          <>
            {/* Frosted glass HUD header — season + location + soft glow */}
            <div className="relative overflow-hidden rounded-3xl p-5 sm:p-6"
              style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', border: '1px solid rgba(255,255,255,0.55)', boxShadow: '0 10px 40px -18px rgba(26,67,49,0.35)' }}
              data-testid="planner-header">
              <div className="absolute -top-20 -right-10 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(180,211,48,0.4), transparent 68%)' }} />
              <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(138,180,248,0.22), transparent 70%)' }} />
              <div className="relative flex items-center gap-4">
                <button onClick={() => { setSelected(null); setPlanner(null); }} data-testid="planner-back-button"
                  className="w-11 h-11 rounded-full bg-white/70 border border-white/60 flex items-center justify-center hover:bg-white transition-colors flex-shrink-0 shadow-sm">
                  <ArrowLeft className="w-5 h-5 text-[#1A4331]" />
                </button>
                <div className="min-w-0 flex-1">
                  <h1 className="text-3xl sm:text-4xl font-bold text-[#1A4331] tracking-tight truncate" style={{ fontFamily: 'Cabinet Grotesk, Outfit, sans-serif' }}>{selected.name}</h1>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1A4331] bg-[#B4D330]/25 border border-[#B4D330]/40 px-3 py-1.5 rounded-full" data-testid="header-season">
                      <Sprout className="w-3.5 h-3.5 text-[#6B9F4A]" /> {currentSeason()}
                    </span>
                    {locationTag && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1A4331] bg-white/70 border border-white/60 px-3 py-1.5 rounded-full" data-testid="header-location">
                        <MapPin className="w-3.5 h-3.5 text-[#8AB4F8]" /> {locationTag}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {plannerLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-12 h-12 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div>
              </div>
            ) : planner && (
              <HolographicCanvas
                garden={selected}
                planner={planner}
                companionMap={companionMap}
                successionCrops={successionCrops}
                rotationBeds={rotationBeds}
                rotCheck={rotCheck}
                aiOn={user?.intelligence_enabled !== false}
                onCreateBed={handleCreateBedFromLayout}
                onAssignCrop={assignCrop}
                onRemoveCrop={unassignCrop}
                onRenameBed={handleRenameBed}
                onDeleteBed={handleDeleteBed}
                onUpdateBedSize={handleUpdateBedSize}
                onAutoFill={handleAutoFill}
                onClearBed={handleClearBed}
                onOpenHistory={(bed) => setHistoryBed(bed)}
                onCarryStart={startDrag}
                onCarryEnd={endDrag}
                onPersistLayout={handlePersistLayout}
              />
            )}
          </>
        )}
      </div>

      {historyBed && selected && (
        <HistoryModal
          gardenId={selected.garden_id}
          bed={historyBed}
          cropList={cropList}
          nowYear={rotation?.now_year || new Date().getFullYear()}
          onClose={() => setHistoryBed(null)}
          onChanged={() => fetchRotation(selected.garden_id)}
        />
      )}

      {showSeason && <SeasonalTemplateOverlay user={user} onClose={() => setShowSeason(false)} />}

      {showCalendar && <PlannerCalendar plantings={plantings} onClose={() => setShowCalendar(false)} />}
    </Layout>
  );
}

export default GardenPlanner;
